// ToolkitPage.tsx — the full toolkit, opened from the Today "Your toolkit" action.
// It renders as a PUSHED SCREEN inside the app shell (header + bottom nav stay put,
// like Your stuff), with a plain back arrow rather than a "Today" label. Lock state
// reuses the real isToolUnlocked logic, so progress-gated tools show locked exactly as
// in Your stuff. Tapping an unlocked tool opens the real exercise (via onOpenTool).
import React from 'react';
import { useProgress } from '@/hooks/useProgress';
import { Icon } from '@/components/icons';
import { TOOLS, TOOL_THEME, TOOL_GROUP_ORDER, isToolUnlocked } from '@/data/toolkit';
import { TODAY_ONE_JOB } from '@/config/features';
import { C, SP, R, F, SIZE, W, eyebrow } from './theme';

export function ToolkitPage({ onOpenTool, onClose }: { onOpenTool: (toolId: string) => void; onClose?: () => void }) {
  const progress = useProgress();
  const completedSet = new Set(progress.state.completedSteps);
  const unlockedCount = TOOLS.filter((t) => isToolUnlocked(t, completedSet)).length;
  // TODAY_ONE_JOB: which groups are showing their locked tools (collapsed by default).
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());
  const groups = TOOL_GROUP_ORDER
    .map((group) => ({ group, tools: TOOLS.filter((t) => t.group === group) }))
    .filter((g) => g.tools.length);

  return (
    <div style={{ animation: 'rise .42s cubic-bezier(.2,.7,.3,1)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '4px 20px calc(var(--safe-bottom) + 40px)' }}>
        {onClose && <button onClick={onClose} aria-label="Back" style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.text, display: 'inline-flex', alignItems: 'center', padding: '6px 8px', marginLeft: -8, marginBottom: SP.xs }}>
          <Icon name="chevLeft" size={24} />
        </button>}

        <div style={{ ...eyebrow, color: C.textFaint }}>Your toolkit</div>
        <h1 style={{ fontFamily: F.title, fontSize: SIZE.h1, fontWeight: W.regular, lineHeight: 1.12, letterSpacing: '-0.5px', color: C.text, margin: '6px 0 0' }}>
          Tools to <em style={{ fontStyle: 'italic', fontWeight: W.medium }}>return to.</em>
        </h1>
        <p style={{ fontFamily: F.ui, fontSize: SIZE.body, color: C.textBody, lineHeight: 1.55, margin: '12px 0 0' }}>
          Gentle, practical exercises for moments of distress or when things get tough. {unlockedCount} of {TOOLS.length} are open. The rest unlock as you learn their skill in the programme.
        </p>

        {groups.map(({ group, tools }) => {
          // TODAY_ONE_JOB: locked tools collapse behind their section header — a
          // day-one visitor sees what is OPEN, not a wall of padlocks. The header
          // still names how many are coming ("N unlock later"), and one tap shows
          // them with their unlock lessons, so the promise is kept without the wall.
          const locked = TODAY_ONE_JOB ? tools.filter((t) => !isToolUnlocked(t, completedSet)) : [];
          const showLocked = !TODAY_ONE_JOB || expandedGroups.has(group);
          const visible = showLocked ? tools : tools.filter((t) => isToolUnlocked(t, completedSet));
          return (
          <div key={group} style={{ marginTop: SP.xl }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: SP.sm }}>
              <div style={{ ...eyebrow, color: C.textMuted }}>{group}</div>
              {TODAY_ONE_JOB && locked.length > 0 && (
                <button
                  onClick={() => setExpandedGroups((cur) => { const next = new Set(cur); if (next.has(group)) next.delete(group); else next.add(group); return next; })}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: F.ui, fontSize: SIZE.caption, fontWeight: W.semibold, color: C.textMuted, padding: '2px 0' }}
                >
                  <Icon name="lock" size={12} /> {locked.length} unlock later
                  <Icon name={expandedGroups.has(group) ? 'chevDown' : 'chevRight'} size={13} />
                </button>
              )}
            </div>
            {visible.length === 0 && (
              <p style={{ fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, margin: 0, lineHeight: 1.5 }}>
                These open as you learn their skill in the programme.
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: SP.sm }}>
              {visible.map((t) => {
                const unlocked = isToolUnlocked(t, completedSet);
                const theme = TOOL_THEME[t.theme];
                return (
                  <button
                    key={t.id}
                    onClick={unlocked ? () => onOpenTool(t.id) : undefined}
                    style={{
                      width: '100%', textAlign: 'left', border: 'none',
                      cursor: unlocked ? 'pointer' : 'default', opacity: unlocked ? 1 : 0.62,
                      display: 'flex', alignItems: 'center', gap: SP.md,
                      background: C.surface, borderRadius: R.lg, padding: '14px 15px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    }}
                  >
                    <span style={{ width: 44, height: 44, borderRadius: R.md, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: unlocked ? theme.soft : 'rgba(0,0,0,0.05)', color: unlocked ? theme.fg : C.textMuted }}>
                      <Icon name={unlocked ? t.icon : 'lock'} size={20} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontFamily: F.title, fontSize: SIZE.h3, fontWeight: W.medium, color: C.text, lineHeight: 1.2 }}>{t.title}</span>
                      <span style={{ display: 'block', fontFamily: F.ui, fontSize: SIZE.caption, color: C.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                        {unlocked ? `${t.steps.length} steps · ${t.time}` : `Unlocks when you complete “${t.unlockLesson}”`}
                      </span>
                    </span>
                    <span aria-hidden style={{ color: C.textFaint, flexShrink: 0 }}><Icon name={unlocked ? 'chevRight' : 'lock'} size={16} /></span>
                  </button>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
